import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getStoredUser } from "../../functions/themeAssets";
import { getGameData, saveChallenge, STAGE_PATHS } from "./gameApi";
import { startReport, updateReport } from "./reports";

export const pad = (n) => (n > 9 ? String(n) : `0${n}`);
export const formatClock = (total) => `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;

/** Elapsed time that ticks every second, starting from `start` seconds (the server's elapsed time). */
export function useStopwatch(start, running) {
  const [seconds, setSeconds] = useState(start || 0);

  useEffect(() => {
    if (!running) return undefined;
    const startedAt = Date.now() - (start || 0) * 1000;
    setSeconds(start || 0);
    const id = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [running, start]);

  return seconds;
}

/**
 * Loads one challenge: theme, progress and the challenge's own content.
 * A player who is not on this challenge is sent to the one they are on (the PHP pages did this
 * with header("Location: ...") when their answers for the page were already stored).
 */
export function useStage(stage) {
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const [game, setGame] = useState(null);
  const gameRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return undefined;
    }
    let cancelled = false;

    (async () => {
      try {
        const json = await getGameData(user, stage);
        if (cancelled) return;
        if (json.state.stage !== stage) {
          navigate(STAGE_PATHS[json.state.stage] || "/rules", { replace: true });
          return;
        }
        if (stage === 1) startReport(user, json.state.reportId);
        gameRef.current = json;
        setGame(json);
      } catch (err) {
        if (cancelled) return;
        await Swal.fire("Error", err.message || "Could not load the game", "error");
        navigate("/rules", { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  /**
   * Save this challenge and tell the reports server.
   * Returns the saved progress, or null when saving failed (the page stays and can be submitted again).
   */
  const finish = async (payload) => {
    try {
      const result = await saveChallenge(user, stage, payload);
      await updateReport(user, gameRef.current?.state?.reportId, { points: result.score, time: result.time });
      if (result.stage >= 5) {
        try {
          sessionStorage.setItem("userData", JSON.stringify({ ...getStoredUser(), gameover: 1 }));
        } catch (_) {
          /* ignore */
        }
      }
      return result;
    } catch (err) {
      await Swal.fire("Error", err.message || "Something went wrong", "error");
      return null;
    }
  };

  return { user, game, finish };
}
